import {
  createElement,
  lazy,
  Suspense,
  type ComponentProps,
  type ComponentType,
} from 'react';
import { whenIdle } from '@/utils/whenIdle';
import { EditorPlaceholder } from './FormEditor/EditorPlaceholder';
import {
  AsyncCreatableSelectField,
  CustomCreatableSelectField,
} from './CreatableSelect';
import { CreatableSelectField } from './CreatableSelect';
import {
  InputField,
  InputWithGenerate,
  InputInlineEdit,
  PlainInput,
  TagInput,
  NumberStepper,
} from './InputFields';
import { SelectField } from './SelectField';
import { TextareaField } from './TextareaField';
import { RadioGroupField } from './RadioGroupField';
import { RadioGroupFieldV2 } from './RadioGroupFieldV2';
import { UploadField } from './UploadField';
import { MenuField } from './MenuField';
import { CheckboxField } from './CheckboxField';
import { AsyncSelectField } from './AsyncSelectField';
import { DateField } from './DateField';
import { SliderField } from './SliderField';
import { MonthYearField } from './MonthYearField/MonthYearField';
import { StarField } from './StarField';
import { RatingField } from './RatingField';
import { CheckboxGroupField } from './CheckboxGroupField';
import { KPhoneField } from './KPhoneField';
import { SwitchField } from './SwitchField';
import { DateFieldWithTZ } from './DateField/DateFieldWithTZ';
import { FilterDatePicker } from './DateField/FilterDatePicker';
import { FilterDateRangePicker } from './DateField/FilterDateRangePicker';
import { KSelectField } from '@/ui-library/components/organisms/KFields/KSelectField';
import { KInputField } from '@/ui-library/components/organisms/KFields/KInputField';
import { KAutoComplete } from '@/ui-library/components/organisms/KFields/KAutoComplete';
import { DependentSelectField } from './DependentSelectField';
export { ConditionalFields } from './ConditionalFields';

/**
 * The rich-text editors (tiptap and its extensions, ~0.4 MB) were in the entry
 * chunk: this registry is reached from the eager FormBuilder, and it imported
 * them statically, so every page paid for an editor whether or not a form on
 * it had one. They are lazy now, behind a placeholder of the field's rough
 * height, and fetched in the background once the browser is idle so an editor
 * opened after first paint is usually already cached.
 *
 * The dynamic imports keep the specifiers the static imports had: the
 * circular-dependency baseline is keyed on these edges.
 */
const loadFormEditor = () => import('./FormEditor');
const loadKulaFormEditor = () => import('./FormEditor/KulaFormEditor');
const loadVariableInlineEditor = () =>
  import('./FormEditor/VariableInlineEditor');

const withSuspense = <P extends object>(
  Component: ComponentType<P>,
  minH: string
) => {
  const Lazy = (props: P) =>
    createElement(
      Suspense,
      { fallback: createElement(EditorPlaceholder, { minH }) },
      createElement(Component, props)
    );
  return Lazy;
};

const LazyFormEditor = lazy(() =>
  loadFormEditor().then((module) => ({ default: module.FormEditor }))
);
const LazyKulaFormEditor = lazy(() =>
  loadKulaFormEditor().then((module) => ({ default: module.KulaFormEditor }))
);
const LazyVariableInlineEditor = lazy(() =>
  loadVariableInlineEditor().then((module) => ({
    default: module.VariableInlineEditor,
  }))
);

export const FormEditor = withSuspense<ComponentProps<typeof LazyFormEditor>>(
  LazyFormEditor,
  '120px'
);
const KulaFormEditor = withSuspense<ComponentProps<typeof LazyKulaFormEditor>>(
  LazyKulaFormEditor,
  '120px'
);
const VariableInlineEditor = withSuspense<
  ComponentProps<typeof LazyVariableInlineEditor>
>(LazyVariableInlineEditor, '40px');

/** Fetch every editor chunk; safe to call repeatedly (the browser caches it). */
export const preloadEditors = () =>
  Promise.all([
    loadFormEditor(),
    loadKulaFormEditor(),
    loadVariableInlineEditor(),
  ]).catch(() => {
    // A failed warm-up is harmless: the field retries when it renders.
  });

// Off the critical path, but before most people open a form. Not on the
// first idle callback: during boot the browser is "idle" while it waits on
// the network, and fetching the editors then competed with the page's own
// chunks. A few seconds in, the first screen has rendered.
const EDITOR_PRELOAD_DELAY_MS = 4000;
if (import.meta.env.MODE !== 'test' && typeof window !== 'undefined') {
  window.setTimeout(
    () => whenIdle(() => void preloadEditors(), { timeout: 5000 }),
    EDITOR_PRELOAD_DELAY_MS
  );
}

export const formFieldComponents = {
  input: InputField,
  plainInput: PlainInput,
  tagInput: TagInput,
  variableInput: VariableInlineEditor,
  inputWithGenerate: InputWithGenerate,
  inputWithEdit: InputInlineEdit,
  numberStepper: NumberStepper,
  textArea: TextareaField,
  select: SelectField,
  asyncSelect: AsyncSelectField,
  filterDatePicker: FilterDatePicker,
  filterDateRangePicker: FilterDateRangePicker,
  selectWithCreate: CreatableSelectField,
  asyncSelectWithCreate: AsyncCreatableSelectField,
  selectWithCreateV1: CustomCreatableSelectField,
  // old: legacy for backward compatibility
  radio: RadioGroupField,
  // new: using kula components
  radio2: RadioGroupFieldV2,
  upload: UploadField,
  editor: FormEditor,
  // kula-ai Editor drop-in for richtext custom fields (offers, contacts, etc.)
  editor2: KulaFormEditor,
  menu: MenuField,
  checkbox: CheckboxField,
  date: DateField,
  dateWithTimeZone: DateFieldWithTZ,
  slider: SliderField,
  monthYear: MonthYearField,
  star: StarField,
  rating: RatingField,
  checkboxGroup: CheckboxGroupField,
  phone: KPhoneField,
  switch: SwitchField,
  dependentSelect: DependentSelectField,
  // Add more types as needed...

  // new components
  // KFields

  kSelect: KSelectField,
  kInput: KInputField,
  autoComplete: KAutoComplete,
};
